import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { Box, Drawer, Typography } from "@mui/material";
import { Assignment } from "@mui/icons-material";

import config from "../../../config";
import { isSessionIoV1RenderingEnabled } from "../../../constants/featureFlags";
import useProjectStore from "../../ProjectCanvas/useProjectStore";
import PanelHeader from "../../common/PanelHeader";
import SessionComponentSection from "./SessionComponentSection";
import SessionDocumentSearchBar from "./SessionDocumentSearchBar";
import useSessionComponentActions from "./useSessionComponentActions";
import { SearchContext } from "./SessionComponentBody";
import {
  getDisplayError,
  getStandardInputData,
  getStandardOutputData,
  getStandardExportSummaryData,
  hasIoEnvelope,
  shouldShowStandardExports,
} from "./sessionIoViewModel";
import "./WorkflowProgressView.css";

// Concatenates the user-facing searchable strings for a component into
// a single lowercased blob. Mirrors what SessionComponentBody actually
// renders in the *Standard* view: standard input, standard output, the
// "Available for Next Steps" summary when shown, plus componentId,
// type, and error.
//
// Intentionally excludes Advanced-only io fields (input bindings,
// export map, diagnostics, raw io). Those panels mount only when the
// user flips a body to Advanced; in-Ace marker repaint in JsonPanel
// still highlights matches there once visible.
//
// The io.v1 path is gated on the same feature flag that gates rendering
// (`isSessionIoV1RenderingEnabled(clientId)`), so the corpus and the
// visible panel are always sourced from the same shape — even if the
// flag is disabled via cohort or `wf86.disableSessionIoV1Rendering` in
// localStorage.
//
// Computed once per component identity (see `searchCorpus` memo) so a
// single keystroke does not re-stringify the entire session.
function buildSearchableText(component, useIoPath) {
  const parts = [];
  if (component?.componentId) parts.push(component.componentId);
  if (component?.type) parts.push(component.type);
  const err = getDisplayError(component);
  if (err) parts.push(typeof err === "string" ? err : JSON.stringify(err));
  if (useIoPath && hasIoEnvelope(component)) {
    for (const getter of [getStandardInputData, getStandardOutputData]) {
      try {
        const v = getter(component);
        if (v == null) continue;
        parts.push(typeof v === "string" ? v : JSON.stringify(v));
      } catch (_) {}
    }
    if (shouldShowStandardExports(component)) {
      try {
        const v = getStandardExportSummaryData(component);
        if (v != null) {
          parts.push(typeof v === "string" ? v : JSON.stringify(v));
        }
      } catch (_) {}
    }
  } else {
    const legacy = component?.data?.data ?? component?.data;
    if (legacy && typeof legacy === "object") {
      try {
        parts.push(JSON.stringify(legacy));
      } catch (_) {}
    } else if (typeof legacy === "string") {
      parts.push(legacy);
    }
  }
  return parts.join("\n").toLowerCase();
}

export const buildSectionKey = (component, sessionData) => {
  if (!component) return "";
  const componentId = component.componentId;
  const threadId = component.thread?.threadId ?? "null";
  const reRunId = sessionData?.reRunHistoryId ?? "latest";
  return `${componentId}::${threadId}::${reRunId}`;
};

function SessionDocumentPanel({
  isOpen,
  onClose,
  isExpanded,
  setIsExpanded,
  isSplitView,
  sessionData,
  orderedSessionComponents,
  selectedSectionKey,
  selectionNonce,
  externalSearchQuery,
  projectId,
  sessionId,
  isTest,
  projectTestId,
  isLatestRun,
  reRuns,
  setSessionData,
  setIsLatestRun,
  setLoadReRun,
  triggerRefresh,
  refreshSessions,
  getWorkflowProgressData,
  handleAlterThreadPath,
}) {
  const sectionRefs = useRef(new Map());
  const [expandedOverrides, setExpandedOverrides] = useState({});
  const [highlightedKey, setHighlightedKey] = useState(null);

  const [searchQuery, setSearchQuery] = useState(externalSearchQuery || "");
  const [searchMatchIndex, setSearchMatchIndex] = useState(0);
  const [searchNavTick, setSearchNavTick] = useState(0);

  // Used to gate the io.v1 corpus path so it matches what
  // SessionComponentBody renders (which also reads this flag).
  const clientId = useProjectStore((s) => s.clientId);
  const useIoPath = isSessionIoV1RenderingEnabled(clientId);

  const lastExternalQueryRef = useRef(externalSearchQuery || "");
  useEffect(() => {
    const next = externalSearchQuery || "";
    if (next === lastExternalQueryRef.current) return;
    lastExternalQueryRef.current = next;
    setSearchQuery(next);
    setSearchMatchIndex(0);
    setSearchNavTick((t) => t + 1);
  }, [externalSearchQuery]);

  // Per-component lowercased corpus, rebuilt only when the components
  // array identity changes (i.e. on session refresh). Keystroke search
  // then only does indexOf scans against precomputed strings.
  const searchCorpus = useMemo(() => {
    if (!orderedSessionComponents) return [];
    return orderedSessionComponents.map((component) => ({
      sectionKey: buildSectionKey(component, sessionData),
      text: buildSearchableText(component, useIoPath),
    }));
  }, [orderedSessionComponents, sessionData, useIoPath]);

  const matchedSections = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return [];
    const out = [];
    for (const entry of searchCorpus) {
      let count = 0;
      let pos = entry.text.indexOf(q);
      while (pos !== -1) {
        count += 1;
        pos = entry.text.indexOf(q, pos + q.length);
      }
      if (count > 0) {
        out.push({ sectionKey: entry.sectionKey, count });
      }
    }
    return out;
  }, [searchQuery, searchCorpus]);

  const totalHits = useMemo(
    () => matchedSections.reduce((acc, m) => acc + m.count, 0),
    [matchedSections]
  );

  // Reset / clamp current index when matches change.
  useEffect(() => {
    if (matchedSections.length === 0) {
      setSearchMatchIndex(0);
      return;
    }
    if (searchMatchIndex >= matchedSections.length) {
      setSearchMatchIndex(0);
    }
  }, [matchedSections, searchMatchIndex]);

  const navigateToMatch = useCallback(
    (nextIdx) => {
      if (matchedSections.length === 0) return;
      const wrapped =
        ((nextIdx % matchedSections.length) + matchedSections.length) %
        matchedSections.length;
      setSearchMatchIndex(wrapped);
      setSearchNavTick((t) => t + 1);
    },
    [matchedSections.length]
  );

  // Scrolls a section to the top of the panel. Sections are collapsed by
  // default (only the targeted section's expand override is set), so the
  // body mounts when we set the override and Ace's onLoad may shift layout
  // briefly. Smooth scroll once after a RAF (lets the expand override
  // commit), then a single corrective re-snap at 350ms in case any Ace
  // onLoad changes height. Browser-native scrollIntoView so we don't have
  // to fight offsetTop math.
  const scrollSectionIntoView = useCallback((key) => {
    let cancelled = false;
    const snap = () => {
      if (cancelled) return;
      const node = sectionRefs.current.get(key);
      if (!node || typeof node.scrollIntoView !== "function") return;
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const raf = window.requestAnimationFrame(snap);
    const t = window.setTimeout(snap, 350);
    return [
      () => {
        cancelled = true;
        window.cancelAnimationFrame(raf);
        window.clearTimeout(t);
      },
    ];
  }, []);

  // When the current match changes (or user advances), drive the existing
  // scroll/expand/highlight machinery via the section-key + nonce path.
  const targetMatchKey = matchedSections[searchMatchIndex]?.sectionKey || null;
  useEffect(() => {
    if (!targetMatchKey) return;
    setExpandedOverrides((prev) => ({ ...prev, [targetMatchKey]: true }));
    const cancellers = scrollSectionIntoView(targetMatchKey);
    setHighlightedKey(targetMatchKey);
    const timer = window.setTimeout(() => setHighlightedKey(null), 1500);
    return () => {
      cancellers.forEach((c) => c());
      window.clearTimeout(timer);
    };
  }, [targetMatchKey, searchNavTick, scrollSectionIntoView]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    setSearchMatchIndex(0);
  }, []);

  const actions = useSessionComponentActions({
    projectId,
    sessionId,
    isTest,
    sessionData,
    getWorkflowProgressData,
    triggerRefresh,
    refreshSessions,
  });

  const registerRef = useCallback((key, node) => {
    if (node) {
      sectionRefs.current.set(key, node);
    } else {
      sectionRefs.current.delete(key);
    }
  }, []);

  // Sections start collapsed. Expanding only happens via canvas-node
  // selection, search-match navigation, or explicit header click. This
  // bounds the number of mounted SessionComponentBody instances (and
  // therefore Ace editors) regardless of how many components the
  // session has.
  const isSectionExpanded = useCallback(
    (key) => {
      if (key in expandedOverrides) return expandedOverrides[key];
      return false;
    },
    [expandedOverrides]
  );

  const toggleSection = useCallback((key) => {
    setExpandedOverrides((prev) => {
      const current = key in prev ? prev[key] : false;
      return { ...prev, [key]: !current };
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || !selectedSectionKey) return undefined;
    setExpandedOverrides((prev) => ({ ...prev, [selectedSectionKey]: true }));
    const cancellers = scrollSectionIntoView(selectedSectionKey);
    setHighlightedKey(selectedSectionKey);
    const timer = window.setTimeout(() => setHighlightedKey(null), 1500);
    return () => {
      cancellers.forEach((c) => c());
      window.clearTimeout(timer);
    };
  }, [
    selectedSectionKey,
    selectionNonce,
    isOpen,
    scrollSectionIntoView,
    orderedSessionComponents,
  ]);

  // Search state lives in a context provided by this panel (see render
  // below) so JsonPanel marker repaints don't force the entire body
  // tree to re-render on every keystroke.
  const bodyProps = useMemo(
    () => ({
      sessionData,
      reRuns,
      setSessionData,
      setIsLatestRun,
      setLoadReRun,
      isTest,
      projectTestId,
      handleAlterThreadPath,
    }),
    [
      sessionData,
      reRuns,
      setSessionData,
      setIsLatestRun,
      setLoadReRun,
      isTest,
      projectTestId,
      handleAlterThreadPath,
    ]
  );

  const searchContextValue = useMemo(
    () => ({ searchQuery, searchNavTick }),
    [searchQuery, searchNavTick]
  );

  const drawerWidth =
    isExpanded && !isSplitView
      ? "calc(100vw - 47px)"
      : isSplitView
      ? config.SESSION_LOGS_COMPONENT_INFO_PANE_WIDTH_IN_SPLIT_VIEW
      : config.SESSION_LOGS_COMPONENT_INFO_PANE_WIDTH;

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      disableAutoFocus
      anchor="right"
      hideBackdrop
      variant="persistent"
      transitionDuration={0}
      SlideProps={{ timeout: 0 }}
      PaperProps={{
        sx: {
          width: drawerWidth,
          position: isSplitView ? "absolute" : "fixed",
          borderTopRightRadius: isSplitView ? "8px" : "0px",
          borderBottomRightRadius: isSplitView ? "8px" : "0px",
          top: isSplitView ? 0 : "57px",
          right: 0,
          height: isSplitView ? "100%" : "calc(100% - 57px)",
          transition: "width 0.3s ease-in-out",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <>
        <PanelHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Assignment sx={{ fontSize: "16px", color: "text.primary" }} />
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Session data
              </Typography>
            </Box>
          }
          showExpandButton
          showCloseButton
          isExpanded={isExpanded}
          onExpand={() => setIsExpanded(!isExpanded)}
          onClose={onClose}
        />
        <SessionDocumentSearchBar
          query={searchQuery}
          onQueryChange={setSearchQuery}
          totalHits={totalHits}
          matchedSectionCount={matchedSections.length}
          currentMatchIndex={searchMatchIndex}
          onPrev={() => navigateToMatch(searchMatchIndex - 1)}
          onNext={() => navigateToMatch(searchMatchIndex + 1)}
          onClear={handleClearSearch}
        />
        <SearchContext.Provider value={searchContextValue}>
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              padding: "16px",
              backgroundColor: "background.paper",
            }}
          >
            {(orderedSessionComponents || []).length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "40px 24px",
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 500, marginBottom: "8px" }}
                >
                  No component data yet
                </Typography>
                <Typography variant="body2">
                  Once the workflow starts running, each component will appear
                  here in execution order.
                </Typography>
              </Box>
            ) : (
              orderedSessionComponents.map((component) => {
                const key = buildSectionKey(component, sessionData);
                return (
                  <SessionComponentSection
                    key={key}
                    sectionKey={key}
                    component={component}
                    isExpanded={isSectionExpanded(key)}
                    isSelected={highlightedKey === key}
                    isLatestRun={isLatestRun}
                    onToggleExpand={toggleSection}
                    actions={actions}
                    actionLoading={actions.actionLoading}
                    bodyProps={bodyProps}
                    registerRef={registerRef}
                  />
                );
              })
            )}
          </Box>
        </SearchContext.Provider>
      </>
    </Drawer>
  );
}

SessionDocumentPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  setIsExpanded: PropTypes.func.isRequired,
  isSplitView: PropTypes.bool,
  sessionData: PropTypes.object,
  orderedSessionComponents: PropTypes.array,
  selectedSectionKey: PropTypes.string,
  selectionNonce: PropTypes.number,
  externalSearchQuery: PropTypes.string,
  projectId: PropTypes.string.isRequired,
  sessionId: PropTypes.string.isRequired,
  isTest: PropTypes.bool,
  projectTestId: PropTypes.string,
  isLatestRun: PropTypes.bool,
  reRuns: PropTypes.array,
  setSessionData: PropTypes.func,
  setIsLatestRun: PropTypes.func,
  setLoadReRun: PropTypes.func,
  triggerRefresh: PropTypes.func,
  refreshSessions: PropTypes.func,
  getWorkflowProgressData: PropTypes.func.isRequired,
  handleAlterThreadPath: PropTypes.func.isRequired,
};

export default SessionDocumentPanel;
